import { readArrowLabel } from './arrowTerminalLabels';

function shapeRecords(changes) {
  return [
    ...Object.values(changes.added ?? {}),
    ...Object.values(changes.removed ?? {}),
    ...Object.values(changes.updated ?? {}).flatMap(([, to]) => [to]),
  ].filter(record => record?.typeName === 'shape');
}

export function isPinCardDocumentChange(changes) {
  const shapes = shapeRecords(changes);
  if (!shapes.length) return false;
  return shapes.every(record => record?.type === 'pin-card');
}

export function isPinCardHeightOnlyUpdate(changes) {
  const added = changes.added ?? {};
  const removed = changes.removed ?? {};
  if (Object.keys(added).length || Object.keys(removed).length) return false;

  const updated = Object.values(changes.updated ?? {});
  if (!updated.length) return false;

  return updated.every(([from, to]) => {
    if (from?.typeName !== 'shape' || to?.typeName !== 'shape') return false;
    if (from.type !== 'pin-card' || to.type !== 'pin-card') return false;
    return (
      from.x === to.x &&
      from.y === to.y &&
      from.props?.w === to.props?.w &&
      from.props?.h !== to.props?.h
    );
  });
}

/** Frame moved or resized — child/followed pins need placement page coords refreshed. */
export function isFramePositionChange(changes) {
  const updated = Object.values(changes.updated ?? {});
  if (!updated.length) return false;

  return updated.some(([from, to]) => {
    if (from?.typeName !== 'shape' || to?.typeName !== 'shape') return false;
    if (from.type !== 'frame' || to.type !== 'frame') return false;
    return (
      from.x !== to.x ||
      from.y !== to.y ||
      from.props?.w !== to.props?.w ||
      from.props?.h !== to.props?.h
    );
  });
}

/** Any frame add/update/remove that must sync board.frames metadata. */
export function hasFrameDocumentChange(changes) {
  const records = shapeRecords(changes);
  if (!records.length) return false;
  return records.some(record => record?.type === 'frame');
}

function arrowPointEq(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.x === b.x && a.y === b.y;
}

/**
 * Arrow update that only changes the relationship label (typing in the binding hint).
 * Ignores props.text — ArrowBindingHint clears mid-path text on every commit.
 */
export function isArrowLabelOnlyUpdate(from, to) {
  if (!from || !to) return false;
  if (from.typeName && from.typeName !== 'shape') return false;
  if (to.typeName && to.typeName !== 'shape') return false;
  if (from.type !== 'arrow' || to.type !== 'arrow') return false;
  if (from.x !== to.x || from.y !== to.y || from.rotation !== to.rotation) return false;

  const fromProps = from.props || {};
  const toProps = to.props || {};
  if (fromProps.bend !== toProps.bend) return false;
  if (!arrowPointEq(fromProps.start, toProps.start)) return false;
  if (!arrowPointEq(fromProps.end, toProps.end)) return false;

  const labelChanged = readArrowLabel(from) !== readArrowLabel(to)
    || String(from.meta?.label || '') !== String(to.meta?.label || '')
    || String(from.meta?.startLabel || '') !== String(to.meta?.startLabel || '')
    || String(from.meta?.endLabel || '') !== String(to.meta?.endLabel || '');
  return labelChanged;
}

/** Frame update that only renames (props.name) — typing in the frame title. */
export function isFrameNameOnlyUpdate(from, to) {
  if (!from || !to) return false;
  if (from.typeName && from.typeName !== 'shape') return false;
  if (to.typeName && to.typeName !== 'shape') return false;
  if (from.type !== 'frame' || to.type !== 'frame') return false;
  if (from.x !== to.x || from.y !== to.y || from.rotation !== to.rotation) return false;
  if (from.props?.w !== to.props?.w || from.props?.h !== to.props?.h) return false;
  return (from.props?.name || '') !== (to.props?.name || '');
}

/**
 * Document batch is only label/title typing — use a longer persist debounce.
 * No adds/removes; every update is arrow terminal labels or frame rename.
 */
export function isLabelOnlyDocumentChange(changes) {
  const added = changes?.added ?? {};
  const removed = changes?.removed ?? {};
  if (Object.keys(added).length || Object.keys(removed).length) return false;

  const updated = Object.values(changes?.updated ?? {});
  if (!updated.length) return false;

  return updated.every(([from, to]) => (
    isArrowLabelOnlyUpdate(from, to) || isFrameNameOnlyUpdate(from, to)
  ));
}

/** Hover bring-to-front only changes fractional index — skip persist. */
export function isPinCardIndexOnlyUpdate(changes) {
  const added = changes.added ?? {};
  const removed = changes.removed ?? {};
  if (Object.keys(added).length || Object.keys(removed).length) return false;

  const updated = Object.values(changes.updated ?? {});
  if (!updated.length) return false;

  const pinTypes = new Set(['pin-card', 'suggest-pin', 'conversation-pin']);

  return updated.every(([from, to]) => {
    if (from?.typeName !== 'shape' || to?.typeName !== 'shape') return false;
    if (!pinTypes.has(from.type) || from.type !== to.type) return false;
    if (from.index === to.index) return false;
    return (
      from.x === to.x &&
      from.y === to.y &&
      from.rotation === to.rotation &&
      from.props?.w === to.props?.w &&
      from.props?.h === to.props?.h &&
      from.meta?.activityId === to.meta?.activityId &&
      (from.meta?.frameId || null) === (to.meta?.frameId || null)
    );
  });
}
